import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { Search, User, Calendar, FileText, BarChart3 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import InlineBehavioralChart from '@/components/recruitment/InlineBehavioralChart';
import { useToast } from '@/hooks/use-toast';

interface Candidate {
  id: string;
  full_name: string;
  email: string;
  completed_at?: string;
  created_at?: string;
  scores?: any;
  company_name?: string;
  source: 'assessment';
  status?: string;
}

interface CandidateSelectModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectCandidate: (candidate: Candidate) => void;
}

const CandidateSelectModal: React.FC<CandidateSelectModalProps> = ({
  isOpen,
  onClose,
  onSelectCandidate,
}) => {
  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [filteredCandidates, setFilteredCandidates] = useState<Candidate[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    if (isOpen) {
      fetchCandidates();
    }
  }, [isOpen]);

  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredCandidates(candidates);
    } else {
      const filtered = candidates.filter(
        candidate =>
          candidate.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          candidate.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (candidate.company_name && candidate.company_name.toLowerCase().includes(searchTerm.toLowerCase()))
      );
      setFilteredCandidates(filtered);
    }
  }, [searchTerm, candidates]);

  const fetchCandidates = async () => {
    setLoading(true);
    try {
      // Fetch behavioral assessment submissions only
      const { data: submissionsData, error: submissionsError } = await supabase
        .from('survey_submissions')
        .select(`
          id,
          full_name,
          email,
          completed_at,
          scores,
          status,
          assessment_type
        `)
        .eq('assessment_type', 'behavioral')
        .order('completed_at', { ascending: false });

      if (submissionsError) throw submissionsError;

      const candidates = (submissionsData || []).map(item => ({
        id: item.id,
        full_name: item.full_name,
        email: item.email,
        completed_at: item.completed_at,
        scores: item.scores,
        source: 'assessment' as const,
        status: item.status
      }));
      
      setCandidates(candidates);
      setFilteredCandidates(candidates);
    } catch (error: any) {
      console.error('Error fetching candidates:', error);
      toast({
        title: 'Error',
        description: 'Failed to load candidates',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSelectCandidate = (candidate: Candidate) => {
    onSelectCandidate(candidate);
    onClose();
    setSearchTerm('');
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="w-[calc(100%-2rem)] max-w-4xl h-full md:h-auto md:max-h-[80vh] inset-0 md:inset-auto translate-x-0 translate-y-0 md:left-[50%] md:top-[50%] md:translate-x-[-50%] md:translate-y-[-50%] rounded-none md:rounded-lg overflow-y-auto overscroll-contain">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <User className="h-5 w-5" />
            Select Candidate Profile
            <Badge variant="secondary" className="text-xs">
              {filteredCandidates.length} total
            </Badge>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Search Input */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search by name, email, or company..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>

          {/* Candidates List */}
          <ScrollArea className="h-[400px] border rounded-lg">
            {loading ? (
              <div className="flex items-center justify-center h-40">
                <div className="text-muted-foreground">Loading candidates...</div>
              </div>
            ) : filteredCandidates.length === 0 ? (
              <div className="flex items-center justify-center h-40">
                <div className="text-muted-foreground">
                  {searchTerm ? 'No candidates found matching your search' : 'No candidates found'}
                </div>
              </div>
            ) : (
              <div className="space-y-2 p-4">
                {filteredCandidates.map((candidate) => (
                  <div
                    key={candidate.id}
                    className="border rounded-lg p-4 hover:bg-muted/50 cursor-pointer transition-colors"
                    onClick={() => handleSelectCandidate(candidate)}
                  >
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-center">
                      {/* Candidate Info */}
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <div className="font-semibold text-foreground">
                            {candidate.full_name}
                          </div>
                          <Badge 
                            variant={candidate.source === 'assessment' ? 'default' : 'secondary'} 
                            className="text-xs"
                          >
                            {candidate.source === 'assessment' ? (
                              <><BarChart3 className="h-3 w-3 mr-1" />Assessment</>
                            ) : (
                              <><FileText className="h-3 w-3 mr-1" />Applicant</>
                            )}
                          </Badge>
                        </div>
                        <div className="text-sm text-muted-foreground">
                          {candidate.email}
                        </div>
                        {candidate.status && (
                          <Badge variant="outline" className="text-xs w-fit">
                            {candidate.status}
                          </Badge>
                        )}
                        <div className="flex items-center gap-1 text-xs text-muted-foreground">
                          <Calendar className="h-3 w-3" />
                          {candidate.completed_at 
                            ? `Completed ${formatDate(candidate.completed_at)}`
                            : candidate.created_at 
                            ? `Applied ${formatDate(candidate.created_at)}`
                            : 'No date available'
                          }
                        </div>
                      </div>

                      {/* Behavioral Chart or Placeholder */}
                      <div className="md:col-span-2">
                        {candidate.scores ? (
                          <InlineBehavioralChart scores={candidate.scores} />
                        ) : (
                          <div className="flex items-center justify-center h-16 bg-muted/30 rounded-lg border-2 border-dashed border-muted-foreground/30">
                            <div className="text-xs text-muted-foreground text-center">
                              <FileText className="h-4 w-4 mx-auto mb-1" />
                              No assessment data
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </ScrollArea>

          <div className="flex justify-end">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CandidateSelectModal;